<template>
  <div>
    <div
      v-if="timeline"
      class="timeline"
      style="position: relative"
    >
      <div class="header">
        <div class="level mb-15">
          <div class="level-">
            <h2 class="title is-4 mb-10">
              {{ artist.name }}
            </h2>
          </div>
          <div class="level-right">
            <a
              role="button"
              class="button is-small mr-10"
              @click="startTuto()"
            >
              <span class="icon">
                <i class="mdi mdi-information-outline" />
              </span>
              <span>Show interactive tutorial</span>
            </a>
            <v-edge-cases
              v-if="timeline.edgeCases.length > 0 && showEdgeCases"
              :edge-cases="timeline.edgeCases"
              @select="edgeCases = $event"
            />
          </div>
        </div>
        <div class="header-bottom">
          <div class="artist-stats mb-10">
            <p style="font-size: 14px; width: 450px; margin-right: 20px">
              This visualization highlights artists related to {{ artist.name }}. Each bubble represents a song. They are are organized into three major sections: inspirations, productions et influences.
            </p>
            <div class="artist-stat">
              <svg
                ref="stats-1"
                height="80"
                width="300"
              />
            </div>
            <div class="artist-stat artist-stat-2">
              <svg
                ref="stats-2"
                height="80"
                width="300"
              />
            </div>
          </div>
          <v-search-song
            :songs="timeline.songs.all"
            class="mb-20"
            @select="onSearch($event)"
          />
        </div>
      </div>

      <div class="svg-wrapper">
        <svg
          :width="timeline.width"
          :height="timeline.height"
        >
          <g>
            <g
              v-for="(row, i) in rows"
              :key="i"
              :transform="`translate(0,${timeline.row.height * i})`"
              class="rows"
            >
              <rect
                :id="`timeline-${i}`"
                :width="timeline.width"
                :height="timeline.row.height"
                fill="transparent"
                @click="onSearch(null)"
              />
            </g>

            <!-- Axis -->

            <g class="axis">

              <g
                v-for="(row, i) in rows"
                :key="i"
                :transform="`translate(0,${timeline.row.height * i})`"
              >
                <!-- Past timeline -->
                <line 
                  class="axis-cut"
                  :x1="0" 
                  :x2="timeline.past.width - 6" 
                  :y1="Math.round(timeline.row.height/2)" 
                  :y2="Math.round(timeline.row.height/2)"
                  stroke="#e1e1e1"
                  stroke-width="1"
                  stroke-dasharray="2"
                />
                <line 
                  class="axis-cut"
                  :x1="timeline.past.width - 2" 
                  :x2="timeline.past.width - 7" 
                  :y1="timeline.row.height/2 - 10" 
                  :y2="timeline.row.height/2 + 10"
                  stroke="#777"
                  stroke-width="1"
                />
                <line 
                  class="axis-cut"
                  :x1="timeline.past.width + 2" 
                  :x2="timeline.past.width - 3" 
                  :y1="timeline.row.height/2 - 10" 
                  :y2="timeline.row.height/2 + 10"
                  stroke="#777"
                  stroke-width="1"
                />
                <!-- Timeline -->
                <line 
                  class="axis-x"
                  :x1="timeline.past.width" 
                  :x2="timeline.width - timeline.cover.width - 16" 
                  :y1="Math.round(timeline.row.height/2)" 
                  :y2="Math.round(timeline.row.height/2)"
                  stroke="#b1b1b1"
                  stroke-width="1"
                  stroke-dasharray="2"
                />
              </g>
            </g>

            <!-- Links -->

            <g
              class="links"
              style="pointer-events: none;"
            >
              <g
                v-for="(link, i) in timeline.links"
                :key="i"
                class="link"
              >
                <path
                  :d="timeline.diagonal({source: link.source, target: link.target})"
                  fill="transparent"
                  :opacity="pathOpacity(link)"
                  :stroke="pathStroke(link)"
                  :stroke-width="pathStrokeWidth(link)"
                />
              </g>
            </g>

            <!-- Row Nodes -->

            <g class="nodes">
              <g
                v-for="(row, i) in rows"
                :key="i"
                :class="`nodes-${i}`"
              >
                <g
                  v-for="song in row.songs"
                  :id="`node-${song.id}`"
                  :key="song.id"
                >

                  <circle
                    v-if="song.complete_tree === false"
                    :r="song.radius - 1.2"
                    :cx="song.x"
                    :cy="song.y"
                    fill="transparent"
                    :stroke="nodeStroke(song)"
                    stroke-width="1"
                    :opacity="nodeOpacity(song)"
                    stroke-dasharray="5 1"
                  />

                  <circle
                    class="node"
                    :r="song.radius - 1.2 - (song.complete_tree ? 0 : 2)"
                    :cx="song.x"
                    :cy="song.y"
                    :fill="nodeColor(song)"
                    :stroke="nodeStroke(song)"
                    :stroke-width="nodeStrokeWidth(song)"
                    :opacity="nodeOpacity(song)"
                    @mouseenter="onMouseover(song)"
                    @mouseleave="onMouseover(null)"
                    @click="onClick(song)"
                  />
                        
                </g>
              </g>
            </g>

            <!-- Cover Nodes -->

            <g class="covers">
              <g
                v-for="(covers, level) in timeline.songs.covers"
                :key="level"
                :class="`covers-${level}`"
              >

                <circle
                  v-for="(song, i) in covers"
                  :id="`node-${song.id}`"
                  :key="i"
                  class="node"
                  :r="song.radius - 1.2"
                  :cx="song.x"
                  :cy="song.y"
                  :fill="nodeColor(song)"
                  :stroke="nodeStroke(song)"
                  :stroke-width="nodeStrokeWidth(song)"
                  :opacity="nodeOpacity(song)"
                  @mouseenter="onMouseover(song)"
                  @mouseleave="onMouseover(null)"
                  @click="onClick(song)"
                />
                        
              </g>
            </g>

            <!-- Left Covers Axis -->

            <g 
              class="axis cover-y"
            >
              <line
                :x1="timeline.width - timeline.cover.width - 15"
                :x2="timeline.width - timeline.cover.width - 15"
                :y1="0"
                :y2="timeline.height - 20"
                stroke="#d8d8d8"
                stroke-width="1"
              />
            </g>

            <!-- X Axis -->

            <g 
              v-axis="timeline.axis.x"
              class="axis x"
              :transform="`translate(0,${20})`"
            />

            <g class="axis-old tick">
              <line
                :x1="Math.round(timeline.past.width/2 + 10)"
                :x2="Math.round(timeline.past.width/2 + 10)"
                :y1="14"
                :y2="20"
                stroke="#000"
                stroke-width="1"
              />
              <text
                :x="Math.round(timeline.past.width/2 + 10)"
                :y="11"
                text-anchor="middle"
              >1500 - 1900</text>
            </g>

            <g class="axis-covers tick">
              
              <text
                :x="Math.round(timeline.width - timeline.cover.width/2)"
                :y="11"
                text-anchor="middle"
              >Covers</text>
            </g>

          </g>

        </svg>

        <div class="timeline-labels">
          <div
            v-for="(row, i) in rows"
            :key="i"
            class="timeline-label" 
            :style="{top: `${timeline.row.height * i + timeline.row.height/2 - 12}px`, color: '#000'}"
          >
            <b-tooltip
              :label="row.description"
              type="is-dark"
              position="is-right"
              multilined=""
              dashed
            >
              {{ row.label }}
            </b-tooltip>
          </div>
        </div>
      </div>

      <v-legend
        class="mb-15"
        :artist="artist"
      />

      <portal to="tooltip">
        <v-tooltip
          :song="hoveredSong"
          :artist="artist" 
        />
      </portal>

      <br>

      <div class="columns is-variable is-1">
        <div class="column">
          <v-artist-lists
            ref="lists"
            :artists="timeline.artists"
            :artist-id="artist.id"
            :can-navigate="canNavigate"
            @mouseoverartist="onArtistMouseover"
            @mouseoverrole="onRoleMouseover"
            @mounted="isLoaded('lists')"
          />
        </div>
        <div class="column is-4">
          <v-tree
            :song="selectedSong"
            :mouseover="mouseover"
            :artist-mouseover="artistMouseover"
            :artist="artist"
            :can-navigate="canNavigate"
            @onMouseover="onMouseover"
            @onClick="onTreeClick"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <loading-component />
    </div>
  </div>
</template>

<script>

import TimelineService from "./timeline.js";
import { rgb, select } from "d3";
import variables from "@/assets/scss/_variables.scss";
import Tree from "./tree/Tree.vue";
import ArtistLists from "./ArtistLists.vue";
import Tooltip from "./Tooltip.vue";
import Legend from "./Legend.vue";
import SearchSong from "./SearchSong.vue";
import EdgeCases from "./EdgeCases.vue";
import tuto from "./tuto";
import debounce from "lodash/debounce";
import {Graph} from "./graph";
import { setCookie, getCookie } from "@/services/helpers";

export default {
	components: {
		"v-tree": Tree,
		"v-artist-lists": ArtistLists,
		"v-tooltip": Tooltip,
		"v-legend": Legend,
		"v-search-song": SearchSong,
		"v-edge-cases": EdgeCases,
	},
	directives: {
		axis (el, binding) {
			let ax = select(el).call(binding.value);
			ax.call(g => g.selectAll(".axis .domain").remove());
			ax.call(g => g.selectAll(".axis .tick line").attr("stroke", "#000"));
		}
	},
	props: {
		artist: {
			required: true,
			type: Object
		},
		canNavigate: {
			default: true,
			type: Boolean
		},
		showEdgeCases: {
			default: true,
			type: Boolean
		},
		skippableTuto: {
			default: true,
			type: Boolean
		}
	},
	data () {
		return {
			allowInteraction: true,
			activePanel: 0,
			timeline: null,
			mouseover: null,
			selectedSong: null,
			edgeCases: null,
			roleMouseover: null,
			artistMouseover: {
				id: null,
				level: null
			},
			loaded: {
				lists: false,
				timeline: false
			}
		};
	},
	computed: {
		numberOfOriginalProductions () {
			if (this.timeline == null) {
				return 0;
			}
			return this.timeline.songs.own.reduce((acc, s) => {
				if (s.parent_id == "") {
					return acc + 1;
				}
				return acc;
			},0);
		},
		numberOfAdaptations () {
			if (this.timeline == null) {
				return 0;
			}
			return this.timeline.songs.own.reduce((acc, s) => {
				if (s.parent_id != "") {
					return acc + 1;
				}
				return acc;
			},0);
		},
		rows () {
			return [
				{songs: this.timeline.songs.source, color: rgb(this.timeline.colors[0]).darker(1), label: "Inspirations", description: `Songs at the origin of an adaptation or a recovery in which the artist ${this.artist.name} is involved`},
				{songs: this.timeline.songs.own, color: rgb(this.timeline.colors[1]).darker(1), label: "Productions", description: `Songs in which the artist ${this.artist.name} is involved`},
				{songs: this.timeline.songs.target, color: rgb(this.timeline.colors[2]).darker(1), label: "Influences", description: `Adaptations from songs in which the artist ${this.artist.name} is involved`},
			];
		},
		hasInteraction () {
			return this.mouseover != null || this.selectedSong != null || this.artistMouseover.id != null;
		},
		hoveredSong () {
			return this.mouseover;
		},
		hoveredTreeId () {
			if (this.mouseover == null) {
				return null;
			}
			return this.timeline.songs.all.find(s => s.id == this.mouseover.id).treeId;
		},
		hasSeenTuto() {
			return getCookie("timeline-tuto") == "true" || false;
		}
	},
	mounted () {
		this.rgb = rgb;
		setTimeout(() => {
			(new TimelineService(this.artist)).get().then(timeline => {
				this.timeline = timeline;
				if (this.preselectedSong) {
					this.selectedSong = this.preselectedSong;
				}
				this.isLoaded("timeline");
			});
		}, 100);
	},
	methods: {
		isLoaded (key) {
			this.loaded[key] = true;
			if (this.loaded.lists && this.loaded.timeline) {
				this.$emit("loaded", this.timeline);
				if (!this.hasSeenTuto) {       
					this.startTuto();
				}
				new Graph(this.$refs["stats-1"], [
					["Original productions", this.numberOfOriginalProductions],
					["Adaptations", this.numberOfAdaptations],
					["Covers", this.timeline.songs.covers.own.length],
				], "His types of productions");
				let score = {
					author: 0,
					composer: 0,
					interpret: 0
				};
				let artist = this.timeline.artists.find(a => a.id == this.artist.id);
				if (artist) {
					score = artist.scores[1];
				}
				new Graph(this.$refs["stats-2"], [
					["Author", score.author, "#4299e1"],
					["Composer", score.composer, "#9f7aea"],
					["Interpreter", score.interpret, "#38b2ac"],
				], "His roles");
			}
		},
		startTuto () {       
			tuto(this);
			setCookie("timeline-tuto", true, 10);
		},
    
		/*
    |--------------------------------------------------------------------------
    | Node States
    |--------------------------------------------------------------------------
    */

		isRelatedTo (trigger, song) {
			if (this[trigger] != null) {
				if (this[trigger].id == song.id) {
					return true;
				}
				if (this[trigger].root_id == song.root_id) {
					return true;
				}
			}
			return false;
		},
		nodeHover (song) {
			if (this.mouseover == null) {
				return false;
			}
			if (this.mouseover.cover) {
				return this.mouseover.cover_artist_id == song.cover_artist_id && this.mouseover.id == song.id;
			} 
			return !song.cover && this.mouseover.id == song.id;
		},
		nodeHoverRelated (song) {
			if (this.isRelatedTo("mouseover", song)) {
				return true;
			}
			return false;
		},
		nodeSelected (song) {
			if (this.selectedSong == null) {
				return false;
			}
			if (this.selectedSong.cover) {
				return this.selectedSong.cover_artist_id == song.cover_artist_id && this.selectedSong.id == song.id;
			}
			return !song.cover && this.selectedSong.id == song.id;
		},
		nodeSelectedRelated (song) {
			if (this.isRelatedTo("selectedSong", song)) {
				return true;
			}
			return false;
		},
		nodeHoverArtist (song) {
			if (this.artistMouseover != null && song.level == this.artistMouseover.level) {
				if (this.roleMouseover == null) {
					if (song.cover) {
						if (song.cover_artist_id == this.artistMouseover.id) {
							return true;
						}
					} else {
						if (song.original_artist_ids.includes(this.artistMouseover.id)) {
							return true;
						}
					}
				}

				if (this.roleMouseover != null) {
					if (this.roleMouseover == "interpret" && song.cover && song.cover_artist_id == this.artistMouseover.id) {
						return true;
					}
					if (song.original_artist_ids.includes(this.artistMouseover.id) && !song.cover && song[this.roleMouseover + "_ids"].includes(this.artistMouseover.id)) {
						return true;
					}

				}
			}

			return false;
		},
		nodeEdgeCase (song) {
			if (this.edgeCases != null && !song.cover) {
				if (this.edgeCases.data.includes(song.id)) {
					return true;
				}
			}
			return false;
		},
    
		/*
    |--------------------------------------------------------------------------
    | Node Design
    |--------------------------------------------------------------------------
    */

		nodeColor (song) {
			if (this.nodeEdgeCase(song)) {
				return "#d90e00";
			}

			if (
				this.nodeHover(song) || 
        this.nodeHoverRelated(song) || 
        this.nodeHoverArtist(song) ||
        this.nodeSelected(song) || 
        this.nodeSelectedRelated(song)
			) {
				return rgb(this.timeline.colors[song.level]).darker(0.6);
			}
			return this.timeline.colors[song.level];
		},
		nodeStroke (song) {
			if (
				this.nodeSelected(song) || 
				this.nodeHover(song) ||
        this.nodeEdgeCase(song) ||
        this.nodeHoverArtist(song)
			) {
				return "#000";
			}
			return rgb(this.timeline.colors[song.level]).darker(2);
		},
		nodeStrokeWidth (song) {
			if (
				this.nodeSelected(song) || 
				this.nodeHover(song) ||
        this.nodeEdgeCase(song) ||
        this.nodeHoverArtist(song)
			) {
				return 2;
			}
			return 1;
		},
		nodeOpacity (song) {
			if (
				this.nodeHover(song) || 
        this.nodeEdgeCase(song) ||
        this.nodeHoverRelated(song) || 
        this.nodeHoverArtist(song) ||
        this.nodeSelected(song) || 
        this.nodeSelectedRelated(song)
			) {
				return 1;
			}
			if (this.selectedSong != null) {
				return 0.55;
			}
			return 1;
		},
    
		/*
    |--------------------------------------------------------------------------
    | Path States
    |--------------------------------------------------------------------------
    */

		pathSelected (link) {
			if (this.selectedSong == null) {
				return false;
			}
      
			return (
				this.selectedSong.root_id == link.source.root_id || 
        this.selectedSong.root_id == link.target.root_id
			);
		},
		pathHover (link) {
			if (this.mouseover == null) {
				return false;
			}
      
			return (
				this.mouseover.root_id == link.source.root_id || 
        this.mouseover.root_id == link.target.root_id
			);
		},
		pathEdgeCase (link) {
			if (
				this.edgeCases != null && 
        this.edgeCases.data.length >= 2 && 
        this.edgeCases.data[1] == link.source.id && 
        this.edgeCases.data[0] == link.target.id
			) {
				return true;
			}
			return false;
		},
    
		/*
    |--------------------------------------------------------------------------
    | Path Design
    |--------------------------------------------------------------------------
    */

		pathOpacity (link) {
			if (this.pathHover(link) || this.pathEdgeCase(link) || this.pathSelected(link)) {
				return 1;
			}
			return 0.1;
		},
		pathStroke (link) {
			if (this.pathEdgeCase(link)) {
				return "#d90e00";
			}
			let color = this.timeline.colors[Math.max(link.source.level, link.target.level)];
			if (this.pathHover(link) || this.pathSelected(link)) {
				return rgb(color).darker(0.2);
			}
			return color;
		},
		pathStrokeWidth (link) {
			if (this.pathHover(link) || this.pathEdgeCase(link)) {
				return 2;
			}
			if (this.pathSelected(link)) {
				return 1.2;
			}
			return 1;
		},
    
		/*
    |--------------------------------------------------------------------------
    | Events
    |--------------------------------------------------------------------------
    */

		onSearch (value) {
			this.selectedSong = value;
		},
		onTreeClick (value) {
			if (this.allowInteraction) {
			  this.selectedSong = value;
			}
		},
		onClick (value) {
			if (this.allowInteraction) {
			  this.selectedSong = value;
			}
		},
		onMouseover (value) {
			if (this.allowInteraction) {
				this.mouseover = value;
			}
		},
		onArtistMouseover (payload) {
			if (this.allowInteraction) {
				this.artistMouseover = payload;
			}
		},
		onRoleMouseover (payload) {
			if (this.allowInteraction) {
				this.roleMouseover = payload;
			}
		},
		onTutoEnd (payload) {
			this.allowInteraction = true;
			this.$emit("tutoEnd");
		}
	}
};
</script>

<style lang="scss">
.svg-wrapper {
  position: relative;
}
.timeline-label {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #fff;
  padding-right: 2px;
  font-size: 14px;
  font-weight: 500;
}
.node {
  cursor: pointer;
}
.tick {
  text {
    font-size: 11px;
    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  }
}
.header {
  display: block;
  margin-bottom: 10px;
}
.artist-stats {
  display: flex;
}
</style>